import { cache } from '@common/util/cache.operator';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';

/**
 * LoadingError
 * Represents the structured error information associated with a LoadingState.
 * Contains a general error code describing the failure and an optional collection
 * of more detailed error codes providing additional context.
 */
export interface LoadingError<ErrorType> {
  generalErrorCode: ErrorType;
  detailedErrorCodes?: ErrorType[];
}

/**
 * LoadingState
 * Keeps track of the state during an asynchronous operation. Including whether the operation is underway or has completed. And any errors associated with the operation.
 * Provides observables for easy use with `OnPush` components.
 */
export class LoadingState<ErrorType> {
  /** Whether the LoadingState has completed. */
  complete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** The current detailed errors of the LoadingState. */
  detailedErrors$: BehaviorSubject<ErrorType[]> = new BehaviorSubject<ErrorType[]>(null);
  /** The current general error of the LoadingState. */
  generalError$: BehaviorSubject<ErrorType> = new BehaviorSubject<ErrorType>(null);
  /** Whether the LoadingState is loading. */
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Sets the loading and error states.
   * Emits the loading value on the loading$ observable and the error values on the error observables.
   * @return Returns this instance to allow chaining.
   */
  update(loading: boolean, generalErrorCode: ErrorType = null, detailedErrorCodes: ErrorType[] = null) {
    this.loading$.next(loading);
    this.generalError$.next(generalErrorCode);
    this.detailedErrors$.next(detailedErrorCodes);
    return this;
  }

  /**
   * Sets the complete state.
   * Emits the complete value on the complete$ observable.
   * @return Returns this instance to allow chaining.
   */
  complete(complete: boolean = true) {
    this.complete$.next(complete);
    return this;
  }
}

/**
 * Represents a combination of multiple LoadingStates. Useful for components that depend on multiple asynchronous operations and need to reflect their combined state.
 */
export interface JoinedLoadingState<ErrorType> {
  /** Whether the JoinedLoadingState has completed. */
  complete$: Observable<boolean>;
  /** The current detailed errors of the JoinedLoadingState. */
  detailedErrors$: Observable<ErrorType[]>;
  /** The current general error of the JoinedLoadingState. */
  generalError$: Observable<ErrorType>;
  /** Whether the JoinedLoadingState is loading. */
  loading$: Observable<boolean>;
  /** The LoadingStates that were joined to create this JoinedLoadingState. */
  loadingStates: LoadingState<ErrorType>[];
}

interface JoinLoadingStatesOptions<ErrorType> {
  /**
   * The LoadingStates to use for error tracking.
   * If not provided, all the LoadingStates being joined will be used.
   * If an empty array is provided, no error tracking will be performed.
   */
  errorStates?: LoadingState<ErrorType>[];
}

/**
 * Joins multiple LoadingStates into a single JoinedLoadingState.
 * The JoinedLoadingState reflects the combined state of the provided LoadingStates.
  * - loading$: true if any of the provided LoadingStates are loading unless one or more of the LoadingStates is in error.
  * - generalError$: the first non-null general error from the provided LoadingStates.
  * - detailedErrors$: a flattened array of all detailed errors from the provided LoadingStates.
  * - complete$: true if all of the provided LoadingStates are complete.
 * @param args An array of LoadingStates to join, optionally followed by JoinLoadingStatesOptions.
 * @returns A JoinedLoadingState representing the combined state.
 */
export function joinLoadingStates<ErrorType>(...args: Array<LoadingState<ErrorType> | JoinLoadingStatesOptions<ErrorType>>): JoinedLoadingState<ErrorType> {
  const lastArg = args[args.length - 1];
  const hasOptions = !!lastArg && !(lastArg instanceof LoadingState) && !(lastArg as LoadingState<ErrorType>)?.loading$;
  const options = hasOptions ? (lastArg as JoinLoadingStatesOptions<ErrorType>) : undefined;
  const loadingStates = (hasOptions ? args.slice(0, -1) : args) as LoadingState<ErrorType>[];
  const errorStates = options?.errorStates ?? loadingStates;

  if (!loadingStates || loadingStates.length === 0) {
    return {
      loadingStates: [],
      loading$: of(false),
      generalError$: of(null),
      detailedErrors$: of(null),
      complete$: of(false)
    };
  }

  const loading$ = combineLatest([
    combineLatest(loadingStates.map(loadingState => loadingState.loading$)),
    combineLatest(loadingStates.map(loadingState => loadingState.generalError$)),
    combineLatest(loadingStates.map(loadingState => loadingState.detailedErrors$))
  ]).pipe(
    map(([loadingValues, generalErrors, detailedErrors]) => {
      const hasAnyError =
        generalErrors.some(error => error != null) ||
        detailedErrors.some(errors => Array.isArray(errors) && errors.length > 0);

      return !hasAnyError && loadingValues.some(Boolean);
    }),
    cache()
  );

  const generalError$ = errorStates.length > 0
    ? combineLatest(errorStates.map(loadingState => loadingState.generalError$)).pipe(
      map(generalErrors => generalErrors.find(error => error != null) ?? null),
      cache()
    )
    : of(null);

  const detailedErrors$ = errorStates.length > 0
    ? combineLatest(errorStates.map(loadingState => loadingState.detailedErrors$)).pipe(
      map(detailedErrors => {
        const flattenedErrors = detailedErrors
          .filter(errors => Array.isArray(errors) && errors.length > 0)
          .reduce((acc, errors) => acc.concat(errors), [] as ErrorType[]);

        return flattenedErrors.length > 0 ? flattenedErrors : null;
      }),
      cache()
    )
    : of(null);

  const complete$ = combineLatest(loadingStates.map(loadingState => loadingState.complete$)).pipe(
    map(completeValues => completeValues.every(Boolean)),
    cache()
  );

  return {
    loadingStates,
    loading$,
    generalError$,
    detailedErrors$,
    complete$
  };
}
